import { type ComponentType, type JSX, lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";

import { ConfirmProvider } from "./components/ConfirmDialog";
import { ToastProvider } from "./components/InlineToast";
import { RouteSkeleton } from "./components/RouteSkeleton";
import { StagingBanner } from "./components/StagingBanner";
import { RequireAdmin, RequireAuth, RequirePermission } from "./lib/auth";
import { reloadOnceForStaleChunk } from "./lib/chunk-errors";
import { HealthRoute } from "./routes/health";

/**
 * lazyRoute — como React.lazy, pero si el dynamic import() falla por un
 * chunk viejo en cache (deploy reciente con hashes nuevos) recarga UNA vez
 * para tomar el index.html nuevo, en lugar de propagar el error al
 * ErrorBoundary raíz (que reemplazaría TODO el shell por ErrorView y dejaría
 * al usuario sin sidebar). Un fallo de import lazy lo captura React como
 * error de render y NO dispara `unhandledrejection`, así que el listener
 * global de main.tsx no lo veía — por eso la recuperación va acá.
 */
function lazyRoute<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    factory().catch((err: unknown) => {
      if (reloadOnceForStaleChunk(err)) {
        // Promesa que nunca resuelve: el <Suspense> sigue mostrando el
        // skeleton mientras el browser despacha el reload (sin flash).
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }),
  );
}

const LandingRoute = lazyRoute(() => import("./features/landing/LandingRoute"));
const LoginRoute = lazyRoute(() => import("./features/login/LoginRoute"));
const ForgotPasswordRoute = lazyRoute(
  () => import("./features/login/ForgotPasswordRoute"),
);
const ResetPasswordRoute = lazyRoute(
  () => import("./features/login/ResetPasswordRoute"),
);
const DashboardLayout = lazyRoute(
  () => import("./features/dashboard/DashboardLayout"),
);
const DashboardHome = lazyRoute(
  () => import("./features/dashboard/DashboardHome"),
);
const ProfileRoute = lazyRoute(() => import("./features/profile/ProfileRoute"));
const AdminUsersRoute = lazyRoute(() => import("./features/admin/AdminUsersRoute"));
const AdminRolesRoute = lazyRoute(() => import("./features/admin/AdminRolesRoute"));
const AdminRoleDetailRoute = lazyRoute(
  () => import("./features/admin/AdminRoleDetailRoute"),
);
const AdminPermissionsRoute = lazyRoute(
  () => import("./features/admin/AdminPermissionsRoute"),
);
const InviteClaimRoute = lazyRoute(() => import("./features/invite/InviteClaimRoute"));

// Placeholder routes — todos lazy del mismo bundle.
// Feature 018 — POS / Ventas MVP. Reemplaza el PlaceholderModule
// previo por el módulo real (sesión + tablero + carrito).
const PosRoute = lazyRoute(() => import("./features/pos/routes/PosRoute"));
const PosSessionsRoute = lazyRoute(
  () => import("./features/pos/routes/PosSessionsRoute"),
);
const PosSessionDetailRoute = lazyRoute(
  () => import("./features/pos/routes/PosSessionDetailRoute"),
);
const PosOrderDetailRoute = lazyRoute(
  () => import("./features/pos/routes/PosOrderDetailRoute"),
);
const PosReportsRoute = lazyRoute(
  () => import("./features/pos/routes/PosReportsRoute"),
);
const PosReconciliationRoute = lazyRoute(
  () => import("./features/pos/routes/PosReconciliationRoute"),
);
// SalesPlaceholder removido — feature 018 cubre /app/pos como
// módulo de ventas. La ruta /app/sales queda como redirect histórico.
const ReportsPlaceholder = lazyRoute(async () => {
  const m = await import("./features/dashboard/PlaceholderRoute");
  return { default: m.ReportsRoute };
});
// Feature 009 — módulo de clientes B2B real (reemplaza el placeholder).
const CustomersRoute = lazyRoute(() => import("./features/customers/CustomersRoute"));
// Feature 010 — visor del audit log (read-only, gateado por audit.read).
const AuditRoute = lazyRoute(() => import("./features/audit/AuditRoute"));
// Feature 011 — BPM (Buenas Prácticas de Manufactura).
const BpmRoute = lazyRoute(() => import("./features/bpm/BpmRoute"));
const CatalogRoute = lazyRoute(
  () => import("./features/catalog/routes/CatalogRoute"),
);
// Feature 014 — módulo de inventarios.
const InventoryRoute = lazyRoute(
  () => import("./features/inventory/routes/InventoryRoute"),
);
// Feature 020 — Compras / Recepciones.
const PurchasesRoute = lazyRoute(
  () => import("./features/purchases/routes/PurchasesRoute"),
);
// Feature 021 — Cuentas por pagar a proveedor.
const PaymentsRoute = lazyRoute(
  () => import("./features/payments/routes/PaymentsRoute"),
);
// Feature 022 — Reporte P&L.
const DataImportRoute = lazyRoute(
  () => import("./features/dataio/routes/DataImportRoute"),
);
const PnLRoute = lazyRoute(
  () => import("./features/reports/routes/PnLRoute"),
);
// Feature 065 Fase 6 — Reporte consolidado de impuestos.
const TaxesReportRoute = lazyRoute(
  () => import("./features/reports/routes/TaxesReportRoute"),
);
// Pedidos por comercial, con rango de fechas y cobros (semana 6 del piloto).
const ByRepReportRoute = lazyRoute(
  () => import("./features/reports/routes/ByRepReportRoute"),
);
// Cartera: cuánto debe cada cliente, con antigüedad de la deuda.
const ReceivablesRoute = lazyRoute(
  () => import("./features/reports/routes/ReceivablesRoute"),
);
// Feature 026 — Datos fiscales del comercio (POS).
const PosBusinessInfoRoute = lazyRoute(
  () => import("./features/pos/routes/PosBusinessInfoRoute"),
);
const PosLocationsRoute = lazyRoute(
  () => import("./features/pos/routes/PosLocationsRoute"),
);
const DoughTypesRoute = lazyRoute(
  () => import("./features/production/routes/DoughTypesRoute"),
);
const ChecklistItemsRoute = lazyRoute(
  () => import("./features/delivery-routes/routes/ChecklistItemsRoute"),
);
// Capacidad de producción: hora de corte y cupo por día (ítem 10 del piloto).
const CapacityRoute = lazyRoute(
  () => import("./features/production/routes/CapacityRoute"),
);
// Feature 024 — Conteos cíclicos de inventario.
const CountListRoute = lazyRoute(
  () => import("./features/inventory-count/routes/CountListRoute"),
);
const CountNewRoute = lazyRoute(
  () => import("./features/inventory-count/routes/CountNewRoute"),
);
const CountDetailRoute = lazyRoute(
  () => import("./features/inventory-count/routes/CountDetailRoute"),
);
const SuppliersRoute = lazyRoute(
  () => import("./features/purchases/routes/SuppliersRoute"),
);
const OrderNewRoute = lazyRoute(
  () => import("./features/purchases/routes/OrderNewRoute"),
);
const OrderDetailRoute = lazyRoute(
  () => import("./features/purchases/routes/OrderDetailRoute"),
);
// Feature 023 — UI reporte by-supplier-month (US9 P3 de feature 020).
const SupplierMonthReportRoute = lazyRoute(
  () => import("./features/purchases/routes/SupplierMonthReportRoute"),
);
// Feature 027 — B2B Order Workflow.
const B2BOrdersRoute = lazyRoute(
  () => import("./features/b2b-orders/routes/B2BOrdersRoute"),
);
const B2BOrderNewRoute = lazyRoute(
  () => import("./features/b2b-orders/routes/B2BOrderNewRoute"),
);
const B2BOrderDetailRoute = lazyRoute(
  () => import("./features/b2b-orders/routes/B2BOrderDetailRoute"),
);
// Barrido de pedidos por fecha de entrega (2026-08-07): a quién le falta
// meter el pedido para un día dado.
const CustomerSweepRoute = lazyRoute(
  () => import("./features/b2b-orders/routes/CustomerSweepRoute"),
);
// Feature 028 — Rutas de despacho B2B.
const DeliveryRoutesRoute = lazyRoute(
  () => import("./features/delivery-routes/routes/DeliveryRoutesRoute"),
);
const DeliveryAgendaRoute = lazyRoute(
  () => import("./features/delivery-routes/routes/DeliveryAgendaRoute"),
);
const DeliveryFactoryRoute = lazyRoute(
  () => import("./features/delivery-routes/routes/FactoryRoute"),
);
const DeliveryRouteDetailRoute = lazyRoute(
  () => import("./features/delivery-routes/routes/DeliveryRouteDetailRoute"),
);
// Feature 068 — bandeja de arqueos.
const CashSettlementsRoute = lazyRoute(
  () => import("./features/delivery-routes/routes/CashSettlementsRoute"),
);
// Feature 065 Fase 5 — App del repartidor (PWA "Mi ruta").
const MyRouteRoute = lazyRoute(
  () => import("./features/my-route/routes/MyRouteRoute"),
);
// Feature 029 — Canastas de preparación B2B.
const PrepBasketsRoute = lazyRoute(
  () => import("./features/prep-baskets/routes/PrepBasketsRoute"),
);
const PrepBasketDetailRoute = lazyRoute(
  () => import("./features/prep-baskets/routes/PrepBasketDetailRoute"),
);
// Feature 065 Fase 2 — Tablero de producción.
const ProductionBoardRoute = lazyRoute(
  () => import("./features/production/routes/ProductionBoardRoute"),
);
// Feature 030 — Commissions & Executive KPIs.
const CommissionRatesRoute = lazyRoute(
  () => import("./features/commissions/routes/CommissionRatesRoute"),
);
const AdminCommissionsRoute = lazyRoute(
  () => import("./features/commissions/routes/AdminCommissionsRoute"),
);
const MyCommissionsRoute = lazyRoute(
  () => import("./features/commissions/routes/MyCommissionsRoute"),
);
// Pagos al personal — liquidaciones por período (semanal/quincenal) + anticipos.
const AdminPayrollRoute = lazyRoute(
  () => import("./features/payroll/routes/AdminPayrollRoute"),
);
const MyPayrollRoute = lazyRoute(
  () => import("./features/payroll/routes/MyPayrollRoute"),
);
const ExecutiveDashboardRoute = lazyRoute(
  () => import("./features/dashboard-executive/routes/ExecutiveDashboardRoute"),
);
// Feature 055 — Catálogo de impuestos múltiples.
const TaxesAdminRoute = lazyRoute(
  () => import("./features/taxes/routes/TaxesAdminRoute"),
);
// Feature 047 — Control de recipientes físicos B2B.
const B2BContainersRoute = lazyRoute(
  () => import("./features/b2b-containers/routes/B2BContainersRoute"),
);
// Feature 065 Fase 3 — inventario físico de recipientes (censo).
const ContainerInventoryRoute = lazyRoute(
  () => import("./features/b2b-containers/routes/ContainerInventoryRoute"),
);
const ContainerInventoryDetailRoute = lazyRoute(
  () => import("./features/b2b-containers/routes/ContainerInventoryDetailRoute"),
);
// Feature 066 — Inventario de canastas (registro de activos físicos).
const BasketsRoute = lazyRoute(() => import("./features/baskets/routes/BasketsRoute"));
const BasketDetailRoute = lazyRoute(
  () => import("./features/baskets/routes/BasketDetailRoute"),
);
// Feature 049 — vista B2B de ventas POS a crédito.
const B2BPosSalesRoute = lazyRoute(
  () => import("./features/b2b-pos-sales/routes/B2BPosSalesRoute"),
);
const B2BPosSaleDetailRoute = lazyRoute(
  () => import("./features/b2b-pos-sales/routes/B2BPosSaleDetailRoute"),
);
// Feature 031 — Centro de Ayuda y Capacitación.
const HelpLayout = lazyRoute(() =>
  import("./features/help/HelpLayout").then((m) => ({ default: m.HelpLayout })),
);
const HelpLandingRoute = lazyRoute(() => import("./features/help/HelpLandingRoute"));
const HelpPageRoute = lazyRoute(() => import("./features/help/HelpPageRoute"));

/**
 * `App` — root del SPA.
 *
 * Estructura de rutas:
 *   /                       → LandingRoute  (público)
 *   /login                  → LoginRoute    (público)
 *   /invite/:token          → InviteClaimRoute (público — claim flow)
 *   /app                    → DashboardLayout (RequireAuth — shell)
 *     index                 → DashboardHome
 *     /profile              → ProfileRoute
 *     /admin/users          → AdminUsersRoute (RequireAdmin)
 *     /admin/roles          → AdminRolesRoute (RequireAdmin)
 *     /admin/permissions    → AdminPermissionsRoute (RequireAdmin)
 *     /pos|/inventory|/sales|/reports|/customers → Placeholders
 *   /health                 → HealthRoute (smoke route operacional)
 */
export function App() {
  return (
    <BrowserRouter>
      <ConfirmProvider>
       <ToastProvider>
        <StagingBanner />
        <Suspense fallback={<RouteSkeleton variant="generic" />}>
        <Routes>
          <Route
            path="/"
            element={
              <Suspense fallback={<RouteSkeleton variant="landing" />}>
                <LandingRoute />
              </Suspense>
            }
          />
          <Route
            path="/login"
            element={
              <Suspense fallback={<RouteSkeleton variant="login" />}>
                <LoginRoute />
              </Suspense>
            }
          />
          <Route
            path="/invite/:token"
            element={
              <Suspense fallback={<RouteSkeleton variant="login" />}>
                <InviteClaimRoute />
              </Suspense>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <Suspense fallback={<RouteSkeleton variant="login" />}>
                <ForgotPasswordRoute />
              </Suspense>
            }
          />
          <Route
            path="/reset-password"
            element={
              <Suspense fallback={<RouteSkeleton variant="login" />}>
                <ResetPasswordRoute />
              </Suspense>
            }
          />
          <Route
            path="/app"
            element={
              <RequireAuth>
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <DashboardLayout />
                </Suspense>
              </RequireAuth>
            }
          >
            <Route
              index
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <DashboardHome />
                </Suspense>
              }
            />
            <Route
              path="profile"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <ProfileRoute />
                </Suspense>
              }
            />
            <Route
              path="admin/users"
              element={
                <RequireAdmin>
                  <Suspense fallback={<RouteSkeleton variant="app" />}>
                    <AdminUsersRoute />
                  </Suspense>
                </RequireAdmin>
              }
            />
            <Route
              path="admin/roles"
              element={
                <RequireAdmin>
                  <Suspense fallback={<RouteSkeleton variant="app" />}>
                    <AdminRolesRoute />
                  </Suspense>
                </RequireAdmin>
              }
            />
            <Route
              path="admin/roles/:slug"
              element={
                <RequireAdmin>
                  <Suspense fallback={<RouteSkeleton variant="app" />}>
                    <AdminRoleDetailRoute />
                  </Suspense>
                </RequireAdmin>
              }
            />
            <Route
              path="admin/permissions"
              element={
                <RequireAdmin>
                  <Suspense fallback={<RouteSkeleton variant="app" />}>
                    <AdminPermissionsRoute />
                  </Suspense>
                </RequireAdmin>
              }
            />
            <Route
              path="pos"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <PosRoute />
                </Suspense>
              }
            />
            <Route
              path="pos/sessions"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <PosSessionsRoute />
                </Suspense>
              }
            />
            <Route
              path="pos/sessions/:id"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <PosSessionDetailRoute />
                </Suspense>
              }
            />
            <Route
              path="pos/orders/:id"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <PosOrderDetailRoute />
                </Suspense>
              }
            />
            <Route
              path="pos/reports"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <PosReportsRoute />
                </Suspense>
              }
            />
            <Route
              path="pos/reconciliation"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <PosReconciliationRoute />
                </Suspense>
              }
            />
            {/* /app/sales — redirige a /app/pos. Conservado solo por
                bookmarks históricos previos a feature 018. */}
            <Route path="sales" element={<Navigate to="/app/pos" replace />} />
            <Route
              path="reports"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <ReportsPlaceholder />
                </Suspense>
              }
            />
            <Route
              path="customers"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <CustomersRoute />
                </Suspense>
              }
            />
            <Route
              path="audit"
              element={
                <RequirePermission permission="audit.read">
                  <Suspense fallback={<RouteSkeleton variant="app" />}>
                    <AuditRoute />
                  </Suspense>
                </RequirePermission>
              }
            />
            <Route
              path="bpm/*"
              element={
                <RequirePermission permission="bpm.read">
                  <Suspense fallback={<RouteSkeleton variant="app" />}>
                    <BpmRoute />
                  </Suspense>
                </RequirePermission>
              }
            />
            <Route
              path="catalog"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <CatalogRoute />
                </Suspense>
              }
            />
            <Route
              path="inventory"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <InventoryRoute />
                </Suspense>
              }
            />
            <Route
              path="purchases"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <PurchasesRoute />
                </Suspense>
              }
            />
            <Route
              path="purchases/suppliers"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <SuppliersRoute />
                </Suspense>
              }
            />
            <Route
              path="purchases/orders/new"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <OrderNewRoute />
                </Suspense>
              }
            />
            <Route
              path="purchases/orders/:id"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <OrderDetailRoute />
                </Suspense>
              }
            />
            <Route
              path="purchases/reports/by-supplier-month"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <SupplierMonthReportRoute />
                </Suspense>
              }
            />
            <Route
              path="b2b-orders"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <B2BOrdersRoute />
                </Suspense>
              }
            />
            <Route
              path="b2b-orders/new"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <B2BOrderNewRoute />
                </Suspense>
              }
            />
            {/* Va ANTES de `b2b-orders/:id` por claridad; react-router ya
                rankea el segmento estático por encima del dinámico. */}
            <Route
              path="b2b-orders/barrido"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <CustomerSweepRoute />
                </Suspense>
              }
            />
            <Route
              path="b2b-orders/:id"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <B2BOrderDetailRoute />
                </Suspense>
              }
            />
            {/* Feature 068 — "Despacho" unificado: la Agenda es el landing
                (base path); la lista/historial pasa a /rutas; /agenda queda
                como alias de los enlaces antiguos. */}
            <Route
              path="delivery-routes"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <DeliveryAgendaRoute />
                </Suspense>
              }
            />
            <Route
              path="delivery-routes/agenda"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <DeliveryAgendaRoute />
                </Suspense>
              }
            />
            <Route
              path="delivery-routes/rutas"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <DeliveryRoutesRoute />
                </Suspense>
              }
            />
            <Route
              path="delivery-routes/cash-settlements"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <CashSettlementsRoute />
                </Suspense>
              }
            />
            <Route
              path="delivery-routes/factory"
              element={
                <RequirePermission permission="delivery_routes.close_day">
                  <Suspense fallback={<RouteSkeleton variant="app" />}>
                    <DeliveryFactoryRoute />
                  </Suspense>
                </RequirePermission>
              }
            />
            <Route
              path="delivery-routes/:id"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <DeliveryRouteDetailRoute />
                </Suspense>
              }
            />
            <Route
              path="my-route"
              element={
                <RequirePermission permission="delivery_routes.driver">
                  <Suspense fallback={<RouteSkeleton variant="app" />}>
                    <MyRouteRoute />
                  </Suspense>
                </RequirePermission>
              }
            />
            <Route
              path="prep-baskets"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <PrepBasketsRoute />
                </Suspense>
              }
            />
            <Route
              path="prep-baskets/:date/:productId"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <PrepBasketDetailRoute />
                </Suspense>
              }
            />
            <Route
              path="production"
              element={
                <RequirePermission permission="production.read">
                  <Suspense fallback={<RouteSkeleton variant="app" />}>
                    <ProductionBoardRoute />
                  </Suspense>
                </RequirePermission>
              }
            />
            <Route
              path="b2b-containers"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <B2BContainersRoute />
                </Suspense>
              }
            />
            <Route
              path="container-inventory"
              element={
                <RequirePermission permission="b2b_containers.inventory">
                  <Suspense fallback={<RouteSkeleton variant="app" />}>
                    <ContainerInventoryRoute />
                  </Suspense>
                </RequirePermission>
              }
            />
            <Route
              path="container-inventory/:id"
              element={
                <RequirePermission permission="b2b_containers.inventory">
                  <Suspense fallback={<RouteSkeleton variant="app" />}>
                    <ContainerInventoryDetailRoute />
                  </Suspense>
                </RequirePermission>
              }
            />
            <Route
              path="baskets"
              element={
                <RequirePermission permission="baskets.read">
                  <Suspense fallback={<RouteSkeleton variant="app" />}>
                    <BasketsRoute />
                  </Suspense>
                </RequirePermission>
              }
            />
            <Route
              path="baskets/:id"
              element={
                <RequirePermission permission="baskets.read">
                  <Suspense fallback={<RouteSkeleton variant="app" />}>
                    <BasketDetailRoute />
                  </Suspense>
                </RequirePermission>
              }
            />
            <Route
              path="b2b-pos-sales"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <B2BPosSalesRoute />
                </Suspense>
              }
            />
            <Route
              path="b2b-pos-sales/:id"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <B2BPosSaleDetailRoute />
                </Suspense>
              }
            />
            <Route
              path="payments"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <PaymentsRoute />
                </Suspense>
              }
            />
            {/* Feature 071 — importar/exportar CSV. */}
            <Route
              path="data"
              element={
                <RequirePermission permission="data.import" fallbackRoles={["superuser", "admin"]}>
                  <Suspense fallback={<RouteSkeleton variant="app" />}>
                    <DataImportRoute />
                  </Suspense>
                </RequirePermission>
              }
            />
            <Route
              path="reports/pnl"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <PnLRoute />
                </Suspense>
              }
            />
            <Route
              path="reports/taxes"
              element={
                <RequirePermission permission="reports.read" fallbackRoles={["superuser", "admin", "contador"]}>
                  <Suspense fallback={<RouteSkeleton variant="app" />}>
                    <TaxesReportRoute />
                  </Suspense>
                </RequirePermission>
              }
            />
            {/*
              Cartera va con `b2b.read` y NO con `reports.read`: el comercial
              necesita ver lo que le deben SUS clientes, y `reports.read` hoy no
              lo tiene. El alcance por vendedor lo aplica el servidor, así que
              abrirlo al comercial no le muestra cartera ajena.
            */}
            <Route
              path="reports/cartera"
              element={
                <RequirePermission
                  permission="b2b.read"
                  fallbackRoles={["superuser", "admin", "contador", "comercial"]}
                >
                  <Suspense fallback={<RouteSkeleton variant="app" />}>
                    <ReceivablesRoute />
                  </Suspense>
                </RequirePermission>
              }
            />
            {/*
              Gateado con `b2b_orders.read`, que es lo que exige el endpoint. Con
              `reports.read` la pantalla se vería y el servidor respondería 403,
              que es la clase de error que ya nos costó caro en canastas.
            */}
            <Route
              path="reports/por-comercial"
              element={
                <RequirePermission
                  permission="b2b_orders.read"
                  fallbackRoles={["superuser", "admin", "comercial", "panadero", "empaque"]}
                >
                  <Suspense fallback={<RouteSkeleton variant="app" />}>
                    <ByRepReportRoute />
                  </Suspense>
                </RequirePermission>
              }
            />
            <Route
              path="admin/pos-business"
              element={
                <RequireAdmin>
                  <Suspense fallback={<RouteSkeleton variant="app" />}>
                    <PosBusinessInfoRoute />
                  </Suspense>
                </RequireAdmin>
              }
            />
            <Route
              path="admin/pos-locations"
              element={
                <RequireAdmin>
                  <Suspense fallback={<RouteSkeleton variant="app" />}>
                    <PosLocationsRoute />
                  </Suspense>
                </RequireAdmin>
              }
            />
            {/* Ítem 128 — las preguntas del checklist de ruta se editan acá.
                Antes la tabla solo se podía tocar entrando a la base. */}
            <Route
              path="admin/checklist-ruta"
              element={
                <RequirePermission permission="delivery_routes.admin" fallbackRoles={["superuser", "admin"]}>
                  <Suspense fallback={<RouteSkeleton variant="app" />}>
                    <ChecklistItemsRoute />
                  </Suspense>
                </RequirePermission>
              }
            />
            <Route
              path="admin/dough-types"
              element={
                <RequirePermission permission="production.admin" fallbackRoles={["superuser", "admin"]}>
                  <Suspense fallback={<RouteSkeleton variant="app" />}>
                    <DoughTypesRoute />
                  </Suspense>
                </RequirePermission>
              }
            />
            <Route
              path="admin/production-capacity"
              element={
                <RequirePermission permission="production.admin" fallbackRoles={["superuser", "admin"]}>
                  <Suspense fallback={<RouteSkeleton variant="app" />}>
                    <CapacityRoute />
                  </Suspense>
                </RequirePermission>
              }
            />
            <Route
              path="admin/taxes"
              element={
                <RequireAdmin>
                  <Suspense fallback={<RouteSkeleton variant="app" />}>
                    <TaxesAdminRoute />
                  </Suspense>
                </RequireAdmin>
              }
            />
            <Route
              path="inventory/counts"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <CountListRoute />
                </Suspense>
              }
            />
            <Route
              path="inventory/counts/new"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <CountNewRoute />
                </Suspense>
              }
            />
            <Route
              path="inventory/counts/:id"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <CountDetailRoute />
                </Suspense>
              }
            />
            <Route
              path="admin/commission-rates"
              element={
                <RequirePermission permission="commissions.admin">
                  <Suspense fallback={<RouteSkeleton variant="app" />}>
                    <CommissionRatesRoute />
                  </Suspense>
                </RequirePermission>
              }
            />
            <Route
              path="admin/commissions"
              element={
                <RequirePermission permission="commissions.admin">
                  <Suspense fallback={<RouteSkeleton variant="app" />}>
                    <AdminCommissionsRoute />
                  </Suspense>
                </RequirePermission>
              }
            />
            <Route
              path="profile/commissions"
              element={
                <RequirePermission permission="commissions.read_own">
                  <Suspense fallback={<RouteSkeleton variant="app" />}>
                    <MyCommissionsRoute />
                  </Suspense>
                </RequirePermission>
              }
            />
            <Route
              path="admin/payroll"
              element={
                <RequirePermission permission="payroll.admin">
                  <Suspense fallback={<RouteSkeleton variant="app" />}>
                    <AdminPayrollRoute />
                  </Suspense>
                </RequirePermission>
              }
            />
            <Route
              path="profile/payroll"
              element={
                <RequirePermission
                  permission="payroll.read_own"
                  fallbackRoles={["superuser", "comercial", "domiciliario", "panadero", "empaque"]}
                >
                  <Suspense fallback={<RouteSkeleton variant="app" />}>
                    <MyPayrollRoute />
                  </Suspense>
                </RequirePermission>
              }
            />
            <Route
              path="dashboard/executive"
              element={
                <RequirePermission permission="dashboard.executive_read">
                  <Suspense fallback={<RouteSkeleton variant="app" />}>
                    <ExecutiveDashboardRoute />
                  </Suspense>
                </RequirePermission>
              }
            />
            <Route
              path="help"
              element={
                <Suspense fallback={<RouteSkeleton variant="app" />}>
                  <HelpLayout />
                </Suspense>
              }
            >
              <Route index element={<HelpLandingRoute />} />
              <Route path=":slug" element={<HelpPageRoute />} />
            </Route>
          </Route>
          <Route path="/health" element={<HealthRoute />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
       </ToastProvider>
      </ConfirmProvider>
    </BrowserRouter>
  );
}

function NotFound(): JSX.Element {
  return (
    <main
      style={{
        padding: "var(--s-12) var(--s-5)",
        maxWidth: 520,
        margin: "0 auto",
        textAlign: "center",
      }}
    >
      <h1 style={{ fontSize: "var(--fs-2xl)" }}>404</h1>
      <p style={{ color: "var(--text-dim)", marginTop: "var(--s-3)" }}>
        Esta órbita no existe.
      </p>
      <a
        href="/"
        style={{
          display: "inline-block",
          marginTop: "var(--s-5)",
          color: "var(--accent)",
          fontWeight: 600,
        }}
      >
        Volver al inicio
      </a>
    </main>
  );
}
